import { useEffect } from 'react';
import { useInjectedService } from './useInjectedService';
import { useSystemConfigStore } from '../stores/useSystemConfigStore';
import { CONFIG_EVENTS } from '../constants/configConstants';
import logger from '../services/loggerService';

/**
 * useRealtimeReconciliation Hook
 * 
 * DESIGN RATIONALE:
 * - Bridges the Real-time Layer (WebSockets) with the Data Integrity Layer (Optimistic Store).
 * - Listens for external state changes and triggers direct store reconciliation.
 * - Platform-agnostic: Consumes injected IRealtimeService.
 * - NEW: Direct payload reconciliation to minimize network overhead.
 * - Security First: Strict payload interpretation to prevent unauthorized state injection.
 */
export const useRealtimeReconciliation = () => {
    const realtime = useInjectedService('realtime');
    const { fetchPublicConfig, reconcileWithServerState } = useSystemConfigStore();

    useEffect(() => {
        if (!realtime) return;

        /**
         * Orchestrates the reconciliation flow for system-wide configuration updates.
         */
        const handleConfigUpdate = (data: unknown) => {
            logger.info('Real-time config update received', { context: 'RealtimeReconciliation' });

            // 1. Direct Payload Reconciliation (Performance Optimization)
            // If the event data contains the new config, we reconcile immediately
            if (data && typeof data === 'object' && ('config' in (data as object) || 'general' in (data as object))) {
                logger.debug('Reconciling store with real-time payload');
                reconcileWithServerState(data as Partial<Record<string, unknown>>); // Cast to partial record for reconciliation pass-through
            } else {
                // 2. Fallback: Background Refresh (Reliability Enhancement)
                // If payload is empty or invalid, we fetch the latest official state
                fetchPublicConfig().catch((err: Error) => {
                    logger.error('Failed to reconcile public config on real-time update', err, 'RealtimeReconciliation');
                });
            }
        };

        // Strict event mapping via constants to ensure 0% magical strings
        realtime.on(CONFIG_EVENTS.UPDATED as string, handleConfigUpdate);

        return () => {
            realtime.off(CONFIG_EVENTS.UPDATED as string, handleConfigUpdate);
        };
    }, [realtime, fetchPublicConfig, reconcileWithServerState]);
};
